/**
 * Lead pipeline ordering - the single source of truth for "is this move
 * forward or backward?".
 *
 * 2026-07-19. Extracted from the two hand-maintained copies of
 * `stageRankByCode` that lived in `leads.service.ts` (Gate 2c) and
 * `LeadStatusChanger.tsx`. They were identical by convention only, so a
 * change to one silently desynced the client's pre-flight check from the
 * server's enforcement. Both now import from here.
 *
 * The ladder as operators think about it:
 *
 *     Not Qualified -> Long Follow-up -> Active(Proposal -> Warm -> Hot) -> Won
 *
 * with Lost reachable from anywhere. Only the OPEN portion is ranked here;
 * `won` / `lost` are deliberately absent because their transitions are
 * intercepted by their own dedicated gates (and their own approval kinds)
 * before any rank comparison happens.
 */

/**
 * Parent-status ordering within the OPEN bucket. Unlisted codes rank as
 * `null` ("unclassified") - callers decide how to treat those rather than
 * getting a silently-wrong 0.
 */
export const LEAD_STATUS_RANK: Readonly<Record<string, number>> = {
  not_qualified: 0,
  long_followup: 1,
  active: 2,
};

/**
 * Sub-status ordering *inside* Active. A lead walks Proposal -> Warm -> Hot
 * as it heats up; walking back down is a demotion and needs oversight the
 * same way leaving Active does.
 */
export const ACTIVE_SUB_STATUS_RANK: Readonly<Record<string, number>> = {
  proposal: 0,
  warm: 1,
  hot: 2,
};

/** Rank of a parent status code, or `null` when the code is unclassified. */
export function leadStatusRank(code: string | null | undefined): number | null {
  if (!code) return null;
  return LEAD_STATUS_RANK[code] ?? null;
}

/** Rank of an Active sub-status code, or `null` when unclassified. */
export function activeSubStatusRank(code: string | null | undefined): number | null {
  if (!code) return null;
  return ACTIVE_SUB_STATUS_RANK[code] ?? null;
}

/**
 * True when both endpoints are known Active sub-statuses and the target sits
 * strictly earlier in the ladder (Hot -> Warm, Hot -> Proposal, Warm ->
 * Proposal).
 *
 * Returns `false` for anything unclassified on either side. That is
 * deliberate: an unknown sub-status must not be *assumed* backward, because
 * the caller turns a `true` here into a hard block. Unknown codes keep the
 * pre-2026-07-19 behaviour (direct transition allowed).
 */
export function isBackwardActiveSubStatusMove(
  fromSubStatusCode: string | null | undefined,
  toSubStatusCode: string | null | undefined,
): boolean {
  const fromRank = activeSubStatusRank(fromSubStatusCode);
  const toRank = activeSubStatusRank(toSubStatusCode);
  if (fromRank === null || toRank === null) return false;
  return toRank < fromRank;
}
